import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";

const NotificationSettingsTab = ({
  theme,
  isMobile,
  userRole,
  settingsData,
  loading,
  onRefresh,
  onOpenDefaultRule,
  onOpenRule,
}) => {
  const { showSnackbar } = useStateContext();
  const soundOptions = useMemo(() => settingsData?.soundOptions || [], [settingsData?.soundOptions]);
  const [savingPreference, setSavingPreference] = useState(false);
  const [soundDialogOpen, setSoundDialogOpen] = useState(false);
  const [previewingSoundKey, setPreviewingSoundKey] = useState("");
  const [savingDeadlineTime, setSavingDeadlineTime] = useState(false);
  const [defaultSoundPreset, setDefaultSoundPreset] = useState(
    settingsData?.memberPreference?.SoundPreset || "",
  );
  const [dialogSelectedSoundKey, setDialogSelectedSoundKey] = useState(
    settingsData?.memberPreference?.SoundPreset || "",
  );
  const [deadlineTimeValue, setDeadlineTimeValue] = useState(dayjs().hour(9).minute(0).second(0));
  const previewAudioRef = useRef(null);

  const toTimeInputValue = useCallback((timeValue) => {
    const trimmed = String(timeValue || "").trim();
    const match = trimmed.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (!match) return "09:00";
    return `${match[1]}:${match[2]}`;
  }, []);

  const toTimePickerValue = useCallback((timeValue) => {
    const normalizedTime = toTimeInputValue(timeValue);
    const [hour, minute] = normalizedTime.split(":").map(Number);
    return dayjs().hour(hour).minute(minute).second(0);
  }, [toTimeInputValue]);

  useEffect(() => {
    const nextSoundKey = settingsData?.memberPreference?.SoundPreset || "";
    setDefaultSoundPreset(nextSoundKey);
    setDialogSelectedSoundKey(nextSoundKey);
  }, [settingsData?.memberPreference?.SoundPreset]);

  useEffect(() => {
    setDeadlineTimeValue(toTimePickerValue(settingsData?.deadlineTime));
  }, [settingsData?.deadlineTime, toTimePickerValue]);

  const mandatoryDefaults = useMemo(() => settingsData?.defaults || [], [settingsData?.defaults]);
  const memberRules = useMemo(() => settingsData?.memberRules || [], [settingsData?.memberRules]);

  const groupedRules = useMemo(
    () =>
      memberRules.reduce((accumulator, rule) => {
        const key = rule.ServiceCategory || "Other";
        if (!accumulator[key]) accumulator[key] = [];
        accumulator[key].push(rule);
        return accumulator;
      }, {}),
    [memberRules],
  );

  const formatRuleSummary = (rule) => {
    if (!rule) return "";
    if (rule.TriggerType === "interpretation_start_in_minutes") {
      return `Interpretation starts in ${rule.TriggerValue} minutes`;
    }
    if (rule.TriggerType === "interpretation_end_hours_ago") {
      return `Interpretation ended ${rule.TriggerValue} hours ago`;
    }
    if (rule.TriggerType === "service_deadline_in_days") {
      return `${rule.ServiceCategory} deadline is in ${rule.TriggerValue} day(s)`;
    }
    if (rule.TriggerType === "service_deadline_today") {
      return `${rule.ServiceCategory} deadline is today`;
    }
    if (rule.TriggerType === "service_deadline_days_ago") {
      return `${rule.ServiceCategory} deadline was ${rule.TriggerValue} day(s) ago`;
    }
    return rule.Name;
  };

  const selectedSoundOption = useMemo(
    () => soundOptions.find((option) => option.key === defaultSoundPreset) || null,
    [soundOptions, defaultSoundPreset],
  );

  const stopPreviewAudio = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
      previewAudioRef.current = null;
    }
    setPreviewingSoundKey("");
  };

  const handleOpenSoundDialog = () => {
    setDialogSelectedSoundKey(defaultSoundPreset);
    setSoundDialogOpen(true);
  };

  const handleCloseSoundDialog = () => {
    stopPreviewAudio();
    setSoundDialogOpen(false);
    setDialogSelectedSoundKey(defaultSoundPreset);
  };

  const handleSavePreference = async () => {
    setSavingPreference(true);
    try {
      await api.put("/notificationSettings/member/preferences", {
        SoundPreset: dialogSelectedSoundKey || "",
      });
      const selectedSound = soundOptions.find((option) => option.key === dialogSelectedSoundKey) || null;
      const detail = {
        enabled: true,
        soundKey: selectedSound?.key || "",
        soundUrl: selectedSound?.url || "",
      };
      setDefaultSoundPreset(dialogSelectedSoundKey || "");
      window.localStorage.setItem("ce_portal.notificationSoundPreference", JSON.stringify(detail));
      window.dispatchEvent(new CustomEvent("notification-sound-preference-updated", { detail }));
      await onRefresh();
      setSoundDialogOpen(false);
    } catch (error) {
      console.error("Failed to save notification preference:", error);
      showSnackbar("Failed to save notification preference", theme, "error");
    } finally {
      stopPreviewAudio();
      setSavingPreference(false);
    }
  };

  const handlePreviewSound = async (soundKey) => {
    const selectedSound = soundOptions.find((option) => option.key === soundKey) || null;
    if (!selectedSound?.url) return;

    stopPreviewAudio();

    const audio = new Audio(selectedSound.url);
    previewAudioRef.current = audio;
    setPreviewingSoundKey(soundKey);

    audio.addEventListener("ended", () => setPreviewingSoundKey(""), { once: true });

    try {
      await audio.play();
    } catch (error) {
      setPreviewingSoundKey("");
    }
  };

  const saveDeadlineTime = async (nextValue) => {
    const isValid = Boolean(nextValue && typeof nextValue.isValid === "function" && nextValue.isValid());
    if (!isValid) {
      return;
    }

    const nextTime = nextValue.format("HH:mm");
    if (nextTime === toTimeInputValue(settingsData?.deadlineTime)) {
      return;
    }

    setSavingDeadlineTime(true);
    try {
      await api.put("/notificationSettings/deadline-time", {
        Time: nextTime,
      });
      await onRefresh();
    } catch (error) {
      console.error("Error saving deadline notification time:", error);
    } finally {
      setSavingDeadlineTime(false);
    }
  };

  const handleDeadlineTimeChange = (nextValue) => {
    setDeadlineTimeValue(nextValue);
  };

  useEffect(() => {
    return () => {
      stopPreviewAudio();
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
          Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          System default rules apply to all members. Personal custom rules apply only to your assignments.
        </Typography>
        <Divider sx={{ my: 2 }} />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.alt,
                borderRadius: "10px",
                border: `1px solid ${theme.palette.primary.border2}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
                    System Default Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    These rules apply to everyone. Only Admin can create, edit, or delete them.
                  </Typography>
                </Box>
                <IconButton
                  color="primary"
                  aria-label="Add system default rule"
                  onClick={() => onOpenDefaultRule(null)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
              {mandatoryDefaults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No system default rules yet.
                </Typography>
              ) : (
                mandatoryDefaults.map((rule) => (
                  <Box
                    key={rule.id}
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={600}>{rule.Name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatRuleSummary(rule)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onOpenDefaultRule(rule)}
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.primary.border2}`,
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>

            <Box
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.alt,
                borderRadius: "10px",
                border: `1px solid ${theme.palette.primary.border2}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
                    My Custom Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add dynamic notification rules for your assignments.
                  </Typography>
                </Box>
                <IconButton
                  color="primary"
                  aria-label="Add custom rule"
                  onClick={() => onOpenRule(null)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </Box>

              {memberRules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No custom rules yet.
                </Typography>
              ) : (
                Object.entries(groupedRules).map(([category, rules]) => (
                  <Box key={category} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="body1" fontWeight={600}>{category}</Typography>
                    {rules.map((rule) => (
                      <Box
                        key={rule.id}
                        sx={{
                          p: 1,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.primary.border2}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Typography variant="body1">{rule.Name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rule.TriggerType}
                            {rule.TriggerValue !== null && rule.TriggerValue !== undefined ? `: ${rule.TriggerValue}` : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Switch checked={Boolean(rule.IsEnabled)} disabled />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onOpenRule(rule)}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "12px",
                              border: `1px solid ${theme.palette.primary.border2}`,
                              bgcolor: theme.palette.background.paper,
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>

            <Box
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.alt,
                borderRadius: "10px",
                border: `1px solid ${theme.palette.primary.border2}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
                  Deadline Reminder Time
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose what time of day deadline-based reminders should fire.
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <MobileTimePicker
                  label="Reminder Time"
                  value={deadlineTimeValue}
                  onChange={handleDeadlineTimeChange}
                  onAccept={saveDeadlineTime}
                  disabled={savingDeadlineTime}
                  ampm
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { minWidth: isMobile ? "100%" : 180 },
                    },
                  }}
                />
                {savingDeadlineTime && (
                  <Typography variant="caption" color="text.secondary">
                    Saving...
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                This time is personal to your account. If unset, reminders default to 9:00 AM.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                bgcolor: theme.palette.background.alt,
                borderRadius: "10px",
                border: `1px solid ${theme.palette.primary.border2}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
                  Sound
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose the sound that should play whenever a notification hits you.
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedSoundOption?.label || "No notification sound selected"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {soundOptions.length > 0
                      ? "Open the sound picker dialog to preview and select a sound."
                      : "Upload notification mp3 files to enable sound selection."}
                  </Typography>
                </Box>
                <IconButton
                  color="primary"
                  aria-label="Choose notification sound"
                  onClick={handleOpenSoundDialog}
                  disabled={soundOptions.length === 0}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Dialog
              open={soundDialogOpen}
              onClose={savingPreference ? undefined : handleCloseSoundDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Select Notification Sound</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {soundOptions.map((option) => {
                    const isSelected = dialogSelectedSoundKey === option.key;
                    const isPreviewing = previewingSoundKey === option.key;

                    return (
                      <Box
                        key={option.key}
                        sx={{
                          p: 1.25,
                          borderRadius: "10px",
                          border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.primary.border2}`,
                          bgcolor: isSelected ? theme.palette.action.selected : theme.palette.background.alt,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => setDialogSelectedSoundKey(option.key)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {isSelected ? (
                            <CheckCircleRoundedIcon color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedRoundedIcon color="action" fontSize="small" />
                          )}
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.fileName}
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowRoundedIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePreviewSound(option.key);
                          }}
                          disabled={isPreviewing}
                        >
                          {isPreviewing ? "Playing..." : "Play"}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseSoundDialog} disabled={savingPreference}>Cancel</Button>
                <Button variant="contained" onClick={handleSavePreference} disabled={savingPreference || !dialogSelectedSoundKey}>
                  {savingPreference ? "Saving..." : "Save"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NotificationSettingsTab;